import { Component, OnInit, OnDestroy, ViewEncapsulation, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { DatePipe, formatDate, formatNumber } from '@angular/common';
import { tap, of, Subscription, Subject, finalize } from 'rxjs';
import { map, switchMap, concatMap, takeUntil, debounceTime, distinctUntilChanged, catchError } from 'rxjs/operators';
import { MessageService, FilterService } from 'primeng/api';
import { Title } from "@angular/platform-browser";
import { KeycloakService } from 'keycloak-angular';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { SecureApiService } from 'src/app/services/secure-api.service';
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { DocListEntry, DocTypes } from 'src/app/shared/doc-list-entry';
import { GraphData, DailyGraphData, CrossRateGraphData } from 'src/app/shared/dashboard';
import { Currency } from 'src/app/shared/currency';
import { RestApiService } from 'src/app/services/rest-api.service';

@Component({
  selector: 'app-home-chart-currency-rates',
  templateUrl: './home-chart-currency-rates.component.html',
  styleUrls: ['./home-chart-currency-rates.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class HomeChartCurrencyRatesComponent implements OnInit, OnDestroy, OnChanges {

  private destroy$ = new Subject<void>();

  @Input() today: Date;
  @Input() clean: boolean = true;

  currencyB: number = 0;
  currencyD: number = 0;
  currencyE: number = 0;

  currList: Currency[] = [];

  crossRatesQuery: Subscription = new Subscription();

  crossRateGraphData: CrossRateGraphData[] = [];
  data: GraphData = { labels: [], datasets: [] };

  basicOptions: any = {};

  globalSettinsData: GlobalSettingsData = null;

  private currencyGraph$ = new Subject<{ since: Date, length: number, currency1: number, currency2: number, currency3: number}>();

  constructor(
    private restapi: RestApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,
  ) {

  }

  ngOnInit(): void {
    console.log('*** HomeChartCurrencyRatesComponent ngOnInit:');
    
    this.titleService.setTitle('Курсы валют - домашняя страница');

    const documentStyle = getComputedStyle(document.documentElement);
    const textColor = documentStyle.getPropertyValue('--text-color');
    const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
    const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

    this.basicOptions =
    {
      scales: {
        x: {
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder
          }
        },
        y: {
          type: 'linear',
          display: true,
          position: 'right',
          ticks: {
            color: textColorSecondary
          },
          grid: {
            color: surfaceBorder
          }
        }
      }
    };

    this.crossRatesQuery =
      this.currencyGraph$.pipe(
        debounceTime(100),
        distinctUntilChanged(),

        switchMap(options => this.restapi.getCurrency().pipe(map(data => {
          this.currList = data;
          return options;
        }))),

        concatMap(opt => this.restapi.getCrossRateGraphData(opt.since, opt.length, opt.currency1, opt.currency2, opt.currency3).pipe(

          map(data => this.crossRateGraphData = data),

          finalize(() => {
            if (this.crossRateGraphData) {
              //              console.log(this.crossRateGraphData);
              let curr1 = this.currList.find((curr) => (curr.CURID == opt.currency1));
              let curr2 = this.currList.find((curr) => (curr.CURID == opt.currency2));
              let curr3 = this.currList.find((curr) => (curr.CURID == opt.currency3));

              this.data = {
                labels: [],
                datasets: [
                  {
                    label: curr1.CURCODE + ' - ' + curr1.CURNAME,
                    data: [],
                    fill: false,
                    borderColor: '#4bc0c0',
                    tension: .4,
                  },
                  {
                    label: curr2.CURCODE + ' - ' + curr2.CURNAME,
                    data: [],
                    fill: false,
                    borderColor: '#565656',
                    tension: .4,
                  },
                  {
                    label: curr3.CURCODE + ' - ' + curr3.CURNAME,
                    data: [],
                    fill: false,
                    borderColor: '#277622',
                    tension: .4,
                  }
                ]
              }
              //console.log(this.data);
            }

            this.crossRateGraphData.forEach((element, index) => {
              //console.log(index);
              //console.log(element);

              this.data.labels.push(formatDate(element.GRAPHDATE, 'dd/M', 'uk', 'GMT+2'));
              this.data.datasets[0].data.push(element.CURRATE1);
              this.data.datasets[1].data.push(element.CURRATE2);
              this.data.datasets[2].data.push(element.CURRATE3);
            });
            //console.log(this.data);
          })

        )),

        catchError(err => {
          console.log('*** HomeChartCurrencyRatesComponent currencyGraph$ catchError: ' + JSON.stringify(err));
          return [];
        })

    ).subscribe({

      error: (err) => {
        console.log('*** HomeChartCurrencyRatesComponent currencyGraph$ error: ' + JSON.stringify(err));
        this.messageService.add({ severity: 'error', summary: 'Помилка', detail: 'Помилка завантаження даних для графіка' });
      }

    });

    // Wait for global settings before setting up dependent subscriptions
    this.globalsettingsservice.getObservableGlobalSettings()
      .pipe(
        map(settings => {

          this.globalSettinsData = settings;

          this.currencyB = this.globalSettinsData.balanceCurrencyId;
          this.currencyD = this.globalSettinsData.viewDCurrencyId;
          this.currencyE = this.globalSettinsData.viewECurrencyId;
        }),
        takeUntil(this.destroy$),

        catchError(err => {
          console.log('*** HomeChartCurrencyRatesComponent getObservableGlobalSettings catchError: ' + JSON.stringify(err));
          return [];
        })
      )
      .subscribe({
        next: () => {
          console.log('*** HomeComponent subscribe completed');
          this.currencyGraph$.next({ since: this.today, length: 45, currency1: this.currencyB, currency2: this.currencyD, currency3: this.currencyE });
        },

        error: err => console.error('*** HomeComponent subscribe error: ' + JSON.stringify(err))
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('*** HomeChartCurrencyRatesComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      switch (propName) {
        case 'today':
          this.currencyGraph$.next({ since: new Date(), length: 45, currency1: this.currencyB, currency2: this.currencyD, currency3: this.currencyE });
          break;
      }
    }
  }

  ngOnDestroy(): void {
    console.log("*** HomeChartCurrencyRatesComponent in ngOnDestroy");

    if (this.crossRatesQuery) { this.crossRatesQuery.unsubscribe(); }

    this.destroy$.next();
    this.destroy$.complete();
  }

  selectData(event) {
  //  if (this.data) {
  //    this.messageService.add({ severity: 'info', summary: 'Data Selected', 'detail': this.data.datasets[event.element._datasetIndex].data[event.element._index].toString() });
  //  }
  }

  getSelectedACurrency(event) {
    this.currencyB = event;

    this.currencyGraph$.next({ since: new Date(), length: 45, currency1: this.currencyB, currency2: this.currencyD, currency3: this.currencyE });
  }

  getSelectedBCurrency(event) {
    this.currencyD = event;

    this.currencyGraph$.next({ since: new Date(), length: 45, currency1: this.currencyB, currency2: this.currencyD, currency3: this.currencyE });
  }

  getSelectedCCurrency(event) {
    this.currencyE = event;

    this.currencyGraph$.next({ since: new Date(), length: 45, currency1: this.currencyB, currency2: this.currencyD, currency3: this.currencyE });
  }
}
